import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import Comment from '../CommentContainer/CommentContainer';
import WithCommentsFromStore from '../../HOCs/WithCommentsFromStore/WithCommentsFromStore';
import RepliesCollapse from '../../components/RepliesCollapse/RepliesCollapse';
import { fetchChildCommentsRequest } from '../../store/actions/comments/commentsActions';
import { toggleReplies } from '../../store/actions/ui/uiActions';

const ConnectedComment = WithCommentsFromStore(Comment); // HoC that connects a comment to the store.

const StyledCommentListItem = styled.div`
  position: relative
  z-index: ${({ commentZIndex }) => commentZIndex};
`;

const CommentsListItem = ({ id, commentZIndex }) => {
  // Redux hook/selector for getting the number of children comments from the state
  const commentFromState = useSelector((state) => {
    return state.commenting.comments.find((comment) => comment.id === id);
  });

  const dispatch = useDispatch();

  const repliesCount =
    commentFromState && commentFromState.descendant_ids
      ? commentFromState.descendant_ids.length
      : 0;

  // This is the common ancestor for both the replies collapse toggle and replies list, that's why the controlling logic is here.
  const [repliesCollapsed, setRepliesCollapsed] = useState(true); // state variable for controlling if replies are collapsed or open.
  const collapseThreshold = 1; // number of replies that trigger presence of collapse.
  const hasRepliesCollapse = repliesCount >= collapseThreshold; // flag for whether we need to show replies collapse and matching toggle button.

  // function to toggle the state collapse/open variable
  const handleRepliesToggleClick = () => {
    if (
      // This combination means
      // the children haven't been loaded yet.
      commentFromState.descendant_ids.length > 0 &&
      commentFromState.children.length === 0 &&
      repliesCollapsed
    ) {
      dispatch(
        fetchChildCommentsRequest({
          contentId: commentFromState.content_id,
          parentId: commentFromState.id
        })
      );
    }
    dispatch(
      toggleReplies({
        contentId: commentFromState.content_id,
        communityId: commentFromState.community_id,
        showingReplies: repliesCollapsed,
        numberOfReplies: commentFromState.descendant_ids.length
      })
    );
    setRepliesCollapsed(!repliesCollapsed);
  };

  useEffect(() => {
    if (commentFromState.replyPosted) {
      setRepliesCollapsed(false);
    }
  }, [commentFromState.replyPosted]);

  const renderCommentsListItem = () => {
    const { deleted, children } = commentFromState;

    if (deleted && children.length === 0) return null;

    return (
      <>
        <ConnectedComment
          id={id}
          communityId={commentFromState.community_id}
          hasRepliesCollapse={hasRepliesCollapse}
          repliesCollapsed={repliesCollapsed}
          handleRepliesCollapseClick={handleRepliesToggleClick}
          repliesCount={repliesCount} // Replies count logic is handled here, so it is passed down instead of via the HOC
          commentZIndex={commentZIndex}
        />
        <RepliesCollapse
          id={id}
          hasRepliesCollapse={hasRepliesCollapse}
          repliesCollapsed={repliesCollapsed}
        />
      </>
    );
  };

  // container div gives a cleaner structure in the final DOM (in my opinion), certainly a little easier to debug but isn't strictly needed.
  return (
    <StyledCommentListItem commentZIndex={commentZIndex}>
      {renderCommentsListItem()}
    </StyledCommentListItem>
  );
};

CommentsListItem.propTypes = {
  id: PropTypes.number.isRequired,
  commentZIndex: PropTypes.number
};

CommentsListItem.defaultProps = {
  commentZIndex: null
};

export default CommentsListItem;
